import { useState } from "react";

import { Button, Callout, FormGroup } from "@blueprintjs/core";
import { isAfter } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import {
  couldManageAttendees,
  isPendingBookingRequest,
} from "@/components/helpers/meeting/is-pending-booking-request";
import { Box, Flex } from "@/components/layout/flexbox";
import { AttendeeList } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeList";
import { Card } from "@/components/pieces/roleplays/Card";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { INNER_BORDER_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import {
  InvitationStatus,
  useReInvFailedMeetingAttendeesMutation,
  useRemoveMeetingAttendeesMutation,
} from "@/graphql";
import { Attendee, FullMeeting, UpcomingMeeting } from "@/types/meetings";
import { WorkspaceScheduleJob } from "@/types/workspaces";
import { formatDate, getBrowserTimezone } from "@/utils/datetime";

import { RemoveMeetingAttendeeConfirmation } from "./RemoveMeetingAttendeeConfirmation";

type Meeting = FullMeeting | UpcomingMeeting;

export const InvitedBoxStyle = {
  margin: "8px 0px 0px 0px",
  border: `solid 1px ${INNER_BORDER_COLOR}`,
  borderRadius: 5,
  padding: "8px 16px",
  overflowY: "auto" as const,
  maxHeight: 300,
};

interface ManageAttendeesDialogProps {
  meeting: Meeting;
  open: boolean;
  onAddAttendees: (job?: WorkspaceScheduleJob) => void;
  onClose: () => void;
  attendees: Attendee[];
  onUpdateAttendee: () => void;
  title: string;
  scheduleJob?: WorkspaceScheduleJob | null;
}

export function ManageAttendeesDialog({
  meeting,
  open,
  onAddAttendees,
  onClose,
  attendees = [],
  onUpdateAttendee = () => {},
  title,
  scheduleJob,
}: ManageAttendeesDialogProps) {
  const organizer = meeting?.customer;
  const [attendeeForRemove, setAttendeeForRemove] = useState<Attendee>();
  const [result, setResult] = useState<{
    success: boolean;
    errorMsg?: string;
  } | null>(null);

  const { scheduledAt } = scheduleJob || {};

  const [removeAttendees, { loading }] = useRemoveMeetingAttendeesMutation({
    onCompleted: (data) => {
      if (data?.removeMeetingAttendees?.ok) {
        setResult({ success: true });
        setAttendeeForRemove(undefined);
        onUpdateAttendee();
      } else {
        setResult({
          success: false,
          errorMsg: "Cannot remove this attendee. Please contact us.",
        });
      }
    },
    onError: (error) => {
      setResult({
        success: false,
        errorMsg: error?.message,
      });
    },
  });

  const [reInvFailedMeetingAttendees, { loading: reInvLoading }] =
    useReInvFailedMeetingAttendeesMutation({
      onCompleted: (data) => {
        if (data?.reInvFailedMeetingAttendees?.ok) {
          setResult({ success: true });
          onUpdateAttendee();
          onClose();
        } else {
          setResult({
            success: false,
            errorMsg:
              "Cannot re invite all the failed attendees. Please contact us.",
          });
        }
      },
      onError: (error) => {
        setResult({
          success: false,
          errorMsg: error?.message,
        });
      },
    });

  const onRemoveInvitedAttendee = () => {
    const email = attendeeForRemove?.email;
    if (!email) return;
    removeAttendees({
      variables: {
        meetingId: meeting?.id,
        emails: [email],
      },
    });
  };

  const onReInvFailedAttendees = () => {
    reInvFailedMeetingAttendees({
      variables: {
        meetingId: meeting?.id,
      },
    });
  };

  const handleOnAddAttendees = (job?: WorkspaceScheduleJob) => {
    onAddAttendees(job);
  };

  const allowEditAttendees = couldManageAttendees(meeting);
  const browserTimezone = getBrowserTimezone();
  const meetingDate = meeting?.eventAt
    ? toZonedTime(meeting?.eventAt, browserTimezone)
    : null;
  const now = new Date();
  const isHappenningMeeting = meetingDate
    ? isAfter(now, new Date(meetingDate))
    : false;

  const disabledEdit =
    isPendingBookingRequest(meeting) ||
    !!meeting.canceledAt ||
    !allowEditAttendees ||
    isHappenningMeeting;

  const attendeesPendingByJob = attendees.filter(
    (item) => item?.scheduledJobId?.toString() === scheduleJob?.id?.toString(),
  );
  const invitedAttendees = scheduleJob
    ? attendees.filter(
        (item) =>
          item?.scheduledJobId?.toString() !== scheduleJob?.id?.toString(),
      )
    : attendees;
  const existFailedAttendees = attendees.some(
    (attendee) => attendee?.status === InvitationStatus.Failed,
  );
  const maxHeight = scheduleJob ? 200 : 300;
  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box fontSize={18}>{title}</Box>}
      width="480px"
      onClose={() => {
        setAttendeeForRemove(undefined);
        setResult(null);
        onClose();
      }}
    >
      <Box p={3}>
        <FormGroup
          style={{ marginBottom: 16 }}
          label={
            <Flex justifyContent={"space-between"}>
              <Box>Invited attendees</Box>
              <Box>({invitedAttendees.length + 1})</Box>
            </Flex>
          }
        >
          <Card style={{ ...InvitedBoxStyle, maxHeight: maxHeight }}>
            {existFailedAttendees && (
              <Callout intent="warning" icon={null}>
                Unable to send invites to some attendees!
              </Callout>
            )}
            <AttendeeList
              organizer={organizer}
              attendees={invitedAttendees}
              mode={disabledEdit ? "view" : "edit"}
              onClick={(attendee) => setAttendeeForRemove(attendee)}
              loading={loading}
            />
          </Card>
        </FormGroup>

        {attendeeForRemove && (
          <RemoveMeetingAttendeeConfirmation
            attendee={attendeeForRemove}
            onYes={onRemoveInvitedAttendee}
            onNo={() => {
              setAttendeeForRemove(undefined);
            }}
            loading={false}
            useDialog={false}
          />
        )}

        {result && !result?.success && (
          <Box mt={2}>
            <Callout icon={null} intent="danger">
              {result?.errorMsg}
            </Callout>
          </Box>
        )}

        {scheduleJob ? (
          <FormGroup style={{ marginBottom: 16 }} label={"Invites Pending"}>
            <Flex color={SUBTEXT_COLOR} justifyContent="space-between">
              <Box>
                {scheduledAt &&
                  formatDate(scheduledAt, "MMM d, yyyy 'at' hh:mm a z")}
                {allowEditAttendees && (
                  <a
                    style={{ marginLeft: 2 }}
                    onClick={() => {
                      handleOnAddAttendees(scheduleJob);
                    }}
                  >
                    Change
                  </a>
                )}
              </Box>
              <Box>({attendeesPendingByJob.length})</Box>
            </Flex>
            <Card style={{ ...InvitedBoxStyle, maxHeight: maxHeight }}>
              <AttendeeList
                attendees={attendeesPendingByJob}
                mode="view-with-status"
              />
            </Card>
          </FormGroup>
        ) : null}

        <Flex justifyContent="space-between" alignItems="center" mt={3}>
          {!disabledEdit && (
            <Button
              disabled={!!attendeeForRemove}
              large={true}
              intent="primary"
              style={{ padding: "10px 20px" }}
              onClick={() => handleOnAddAttendees()}
            >
              Add Attendees
            </Button>
          )}

          {!disabledEdit && existFailedAttendees && (
            <Button
              disabled={reInvLoading}
              intent="none"
              onClick={() => onReInvFailedAttendees()}
              large={true}
              style={{ padding: "10px 20px" }}
            >
              Resend failed invitation
            </Button>
          )}
        </Flex>
      </Box>
    </StyledDialog>
  );
}
